import { useCallback, useEffect, useState } from 'react'
import type { DescribeOverview, FilterLibrary, PictureDescription } from '@shared/services'
import { describeAgain, readDescribeOverview, startDescribe, steerDescribe } from '@shared/services'

/**
 * Describe, on the Auto Filter page (Huanrui, 2026-11-03).
 *
 * Gemini writes down what each picture shows, for search to find it by
 * (workers/api/src/describe.ts). Nothing is described by itself: a try of a
 * few pictures to judge by eye, or every picture of the library not described
 * yet -- which, run again later, is only the new ones. The run goes on in the
 * cloud with this page closed; Pause, Continue and Stop act on it from here.
 */

const LIBRARY_NAMES: Record<FilterLibrary, string> = {
  audit: 'Audit',
  'design-works': 'Design References',
  'visual-language': 'Visual Language',
}

/** How often the page reads a run that is going. */
const READ_EVERY_MS = 5000

const usd = (value: number) => (value >= 1 ? `$${value.toFixed(2)}` : `$${value.toFixed(3)}`)

function duration(seconds: number): string {
  if (seconds < 90) return 'about a minute'
  const minutes = Math.round(seconds / 60)
  if (minutes < 90) return `about ${minutes} min`
  const hours = seconds / 3600
  return `about ${hours < 10 ? hours.toFixed(1) : Math.round(hours)} hours`
}

export function AutoFilterDescribe({ library }: { library: FilterLibrary }) {
  const [overview, setOverview] = useState<DescribeOverview | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const reload = useCallback(() => {
    readDescribeOverview(library)
      .then((found) => {
        if (found.library !== library) return
        setOverview(found)
        setError('')
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Could not read the descriptions'))
  }, [library])

  useEffect(() => {
    setOverview(null)
    reload()
  }, [reload])

  // Read again every few seconds while a run is going, and stop when it ends.
  const going = overview?.active ?? null
  useEffect(() => {
    if (!going || going.status !== 'running') return
    const timer = window.setInterval(reload, READ_EVERY_MS)
    return () => window.clearInterval(timer)
  }, [going?.id, going?.status, reload])

  const act = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setError('')
    try {
      await work()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work')
    } finally {
      setBusy(false)
      reload()
    }
  }

  const left = overview ? Math.max(0, overview.pictures - overview.described) : 0
  const per = overview?.perPicture
  const here = going && going.library === library ? going : null
  const elsewhere = going && going.library !== library ? going : null
  const handled = here ? here.done + here.failed : 0
  const remaining = here ? Math.max(0, here.total - handled) : 0

  return (
    <section className="afl__card afl__describe">
      <span className="afl__label">Describe · {overview?.model ?? 'Gemini'}</span>
      <div className="afl__stats">
        <div>
          <strong>{overview ? overview.pictures.toLocaleString() : '…'}</strong>
          <span>{LIBRARY_NAMES[library]} pictures</span>
        </div>
        <div>
          <strong>{overview ? overview.described.toLocaleString() : '…'}</strong>
          <span>described</span>
        </div>
        <div>
          <strong>{overview ? left.toLocaleString() : '…'}</strong>
          <span>not yet</span>
        </div>
      </div>

      {here ? (
        <>
          <div className="afl__progress">
            <div className="afl__track">
              <span style={{ width: `${here.total ? Math.min(100, (handled / here.total) * 100) : 0}%` }} />
            </div>
            <span>
              {handled.toLocaleString()} of {here.total.toLocaleString()} · {usd(here.costUsd)}
            </span>
          </div>
          <div className="afl__controls afl__controls--tight">
            <p className="afl__note afl__note--inline">
              {here.status === 'paused' ? 'Paused' : here.kind === 'try' ? 'Trying' : 'Describing'}
              {here.failed ? ` · ${here.failed} failed` : ''}
              {here.status === 'running' && per && remaining > 0
                ? ` · ${duration((remaining * per.seconds) / (overview?.lanes || 3))} left`
                : ''}
              {' · '}runs in the cloud: this page can be closed.
            </p>
            <span className="afl__spacer" />
            {here.status === 'running' ? (
              <button type="button" className="afl__btn" disabled={busy} onClick={() => void act(() => steerDescribe(here.id, 'pause'))}>
                Pause
              </button>
            ) : (
              <button type="button" className="afl__btn afl__btn--go" disabled={busy} onClick={() => void act(() => steerDescribe(here.id, 'resume'))}>
                Continue
              </button>
            )}
            <button type="button" className="afl__btn" disabled={busy} onClick={() => void act(() => steerDescribe(here.id, 'stop'))}>
              Stop
            </button>
          </div>
        </>
      ) : (
        <div className="afl__controls">
          <p className="afl__note afl__note--inline">
            {elsewhere
              ? `A run is ${elsewhere.status === 'paused' ? 'paused' : 'going'} in ${LIBRARY_NAMES[elsewhere.library]}: one at a time.`
              : overview && per
                ? `${left.toLocaleString()} pictures · about ${usd(left * per.usd)} · ${duration((left * per.seconds) / (overview.lanes || 3))}` +
                  ` · ${per.measured ? `from the last ${per.measured}` : 'estimate from the trial'}`
                : ''}
          </p>
          <span className="afl__spacer" />
          <button
            type="button"
            className="afl__btn"
            disabled={!overview || busy || !!elsewhere || left === 0}
            onClick={() => void act(() => startDescribe(library, 'try'))}
          >
            Try {overview?.tryCount ?? 50}
          </button>
          <button
            type="button"
            className="afl__btn afl__btn--go"
            disabled={!overview || busy || !!elsewhere || left === 0}
            onClick={() => void act(() => startDescribe(library, 'all'))}
          >
            Describe all not yet
          </button>
        </div>
      )}

      {error ? <p className="afl__error">{error}</p> : null}

      {overview && overview.runs.length > 0 ? (
        <div className="afl__runs afl__runs--describe">
          {overview.runs.slice(0, 4).map((run) => (
            <div key={run.id} className="afl__run">
              <span>{run.kind === 'try' ? 'Try' : 'All'}</span>
              <span>
                {(run.done + run.failed).toLocaleString()} of {run.total.toLocaleString()}
                {run.failed ? ` · ${run.failed} failed` : ''} · {usd(run.costUsd)}
              </span>
              <em>{run.status}</em>
            </div>
          ))}
        </div>
      ) : null}

      {overview && overview.latest.length > 0 ? (
        <details className="afl__fold afl__desc-fold" open={!!here && here.kind === 'try'}>
          <summary className="afl__label">Latest descriptions, to check by eye</summary>
          <div className="afl__desc-list">
            {overview.latest.map((one) => (
              <DescriptionCard key={one.assetId} one={one} onChanged={reload} />
            ))}
          </div>
        </details>
      ) : null}
    </section>
  )
}

function DescriptionCard({
  one,
  onChanged,
}: {
  one: PictureDescription & { thumb: string; full: string }
  onChanged: () => void
}) {
  const [shown, setShown] = useState<PictureDescription>(one)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => setShown(one), [one])

  const again = async () => {
    setBusy(true)
    setError('')
    try {
      const { description } = await describeAgain(one.assetId)
      if (description) setShown(description)
      onChanged()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'It could not be described')
    } finally {
      setBusy(false)
    }
  }

  const rows: [string, string][] = [
    ['Brand', shown.brand],
    ['Product', shown.product],
    ['Variant', shown.variant],
    ['Format', shown.format],
    ['Material', shown.material],
    ['Colours', shown.colours],
  ]
  return (
    <div className="afl__desc">
      <a className="afl__desc-pic" href={one.full || one.thumb} target="_blank" rel="noreferrer" title="Open full size">
        {one.thumb ? <img src={one.thumb} alt="" loading="lazy" /> : null}
      </a>
      <div className="afl__desc-body">
        <dl className="afl__desc-fields">
          {rows.map(([label, value]) =>
            value ? (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ) : null,
          )}
          {!shown.isPackaging ? (
            <div>
              <dt>Packaging</dt>
              <dd>No</dd>
            </div>
          ) : null}
        </dl>
        {shown.visibleText.length > 0 ? (
          <p className="afl__desc-text">
            {shown.visibleText.map((line, index) => (
              <span key={index}>{line}</span>
            ))}
          </p>
        ) : null}
        {shown.description ? <p className="afl__desc-sentences">{shown.description}</p> : null}
        {shown.uncertain ? <p className="afl__desc-unsure">Not sure: {shown.uncertain}</p> : null}
        <div className="afl__desc-foot">
          {error ? <span className="afl__error">{error}</span> : null}
          <span className="afl__spacer" />
          <button type="button" className="afl__mini" disabled={busy} onClick={() => void again()}>
            {busy ? 'Describing…' : 'Describe again'}
          </button>
        </div>
      </div>
    </div>
  )
}
